import { datadogRum } from "@datadog/browser-rum";
import { AppRoute } from "app";
import { useAtomValue } from "jotai";
import { useEffect } from "react";
import { useMutation } from "react-query";
import { useNavigate } from "react-router-dom";
import { colors } from "theme";

import Button from "components/Button";
import Checklist from "components/ChecklistItem";
import ClientTranslation from "components/ClientTranslation";
import Flex from "components/Flex";
import { Image } from "components/Image";
import PageLayout from "components/PageLayout";
import Text from "components/Text";

import { startSession } from "api/startSession";
import { clientConfigAtom } from "atoms/clientConfig";
import { sessionIdAtom } from "atoms/sessionId";
import { useClientTranslation } from "hooks/useClientTranslation";
import { useEventTracking } from "hooks/useEventTracking";
import { usePageRouting } from "hooks/usePageRouting";
import { precacheImages } from "utils/imageProcessing/precacheImages";
import { shouldRetry } from "utils/networkRequestHelper";

import { clientIdAtom } from "../../atoms/clientId";
import { documentToImages } from "../AdditionalDocuments/documentToImages";
import { useStyles } from "./styles";

const DEFAULT_IMAGE: Image = {
  src: "/assets/png/onboarding.png",
};

export const Onboarding = () => {
  const {
    onboarding: { checklistItems, illustrations },
    additionalDocuments,
    vin,
  } = useAtomValue(clientConfigAtom);

  const image = {
    ...DEFAULT_IMAGE,
    ...(illustrations?.main ?? {}),
  };

  const sessionId = useAtomValue(sessionIdAtom);
  const clientId = useAtomValue(clientIdAtom);
  const navigate = useNavigate();
  const { onNext } = usePageRouting();
  const { classes } = useStyles({ image });
  const { trackEvent } = useEventTracking();

  const { t } = useClientTranslation();

  const allChecklistItems = [...checklistItems, ...(additionalDocuments?.documents || [])];

  const { isLoading, mutate } = useMutation(() => startSession(sessionId, clientId), {
    retry: shouldRetry,
    onSuccess: () => {
      trackEvent({ name: "Onboarding" });
      onNext();
    },
    onError: (error) => {
      datadogRum.addError(error, {
        message: "Failed to start session",
      });

      navigate(AppRoute.ApiError);
    },
  });

  useEffect(() => {
    if (additionalDocuments) {
      const documents = additionalDocuments.documents || [];
      const additionalPhotos = documents.flatMap(documentToImages).filter((document) => document);
      precacheImages(additionalPhotos);
    }

    if (vin) {
      const vinPhotos = vin.customExampleImages || [];
      precacheImages(vinPhotos);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <PageLayout
      title={t("onboarding.header")}
      dataTestId="onboarding"
      bottomButtons={
        <Button loading={isLoading} onClick={mutate}>
          {t("onboarding.start")}
        </Button>
      }
    >
      <Flex flex="1" py={8} px={24} gap={32}>
        <Flex>
          {
            <Text sizeVariant="md">
              <ClientTranslation i18nKey="onboarding.instructions">
                All you’ll need to do is take photos of your vehicle and answer a few questions.
                <br />
                <br />
                This’ll take about <strong>10 minutes.</strong>
              </ClientTranslation>
            </Text>
          }
        </Flex>
        {allChecklistItems.length > 0 && (
          <Flex gap={16}>
            <Flex gap={8}>
              <Text sizeVariant="lg" color={colors.grey[90]}>
                {t("onboarding.checklist.header")}
              </Text>
              <Text sizeVariant="md" color={colors.grey[80]}>
                {t("onboarding.checklist.instructions")}
              </Text>
            </Flex>
            <Flex as="ul" gap={16}>
              {allChecklistItems.map((item) => (
                <Checklist key={item} variant={item} />
              ))}
            </Flex>
          </Flex>
        )}
      </Flex>
      <div role="img" className={classes.illustration} aria-label="phone in front of car taking photos" />
    </PageLayout>
  );
};
